/**
 * §7.2 screen 1 — Login. Email + argon2id password, plus a one-time code for
 * the accounts that have an authenticator enrolled (migration 0018).
 *
 * THE CODE FIELD IS OPTIONAL AND LOOKS IT. Only the server knows which accounts
 * require one, and it must stay that way: a form that revealed the requirement —
 * by asking for the code on a second step, by marking the field required once an
 * email is typed, or by reporting "this account needs a code" — would be an
 * enumeration oracle built out of UI. So every visitor sees the same optional
 * field, and an account that requires a code and did not get one fails with the
 * ordinary undifferentiated 401.
 *
 * That same 401 is returned for a wrong email, a wrong password, a wrong code
 * and a missing one, and this screen keeps it that way: one message, never a
 * hint about which was wrong.
 */
import { useState, type FormEvent } from 'react';
import { ApiError, errorKey } from '../api/client.js';
import { useSession } from '../auth/SessionContext.js';
import { useI18n } from '../i18n/LocaleContext.jsx';
import { Button, ErrorBanner, Field, TextInput } from '../ui/index.jsx';

export function LoginScreen(): React.JSX.Element {
  const { login } = useSession();
  const { t } = useI18n();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [totp, setTotp] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit(event: FormEvent): Promise<void> {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await login(email, password, totp);
    } catch (caught) {
      if (caught instanceof ApiError && caught.status === 401) {
        // One message for all three causes. Deliberate (§5).
        setError(t('admin.login.failed'));
      } else if (caught instanceof ApiError && caught.status === 429) {
        setError(t('admin.login.locked'));
      } else {
        const { key, values } = errorKey(caught);
        setError(t(key, values));
      }
      setTotp('');
    } finally {
      setBusy(false);
    }
  }

  return (
    <main className="flex min-h-full items-center justify-center bg-attract p-6">
      <form
        onSubmit={(event) => void submit(event)}
        className="flex w-full max-w-md flex-col gap-5 rounded-card bg-surface-0 p-8 shadow-elev-3"
      >
        <header className="flex flex-col gap-1">
          <p className="text-xs uppercase tracking-eyebrow text-ink-500">
            {t('admin.login.subtitle')}
          </p>
          <h1 className="text-xl font-bold text-brand-900">{t('admin.login.title')}</h1>
        </header>

        <ErrorBanner message={error} />

        <Field label={t('admin.login.email')}>
          <TextInput
            type="email"
            autoComplete="username"
            required
            maxLength={254}
            value={email}
            onChange={(event) => setEmail(event.target.value)}
          />
        </Field>

        <Field label={t('admin.login.password')}>
          <TextInput
            type="password"
            autoComplete="current-password"
            required
            minLength={12}
            maxLength={256}
            value={password}
            onChange={(event) => setPassword(event.target.value)}
          />
        </Field>

        <Field label={t('admin.login.totp')} hint={t('admin.login.totpHint')}>
          <TextInput
            inputMode="numeric"
            autoComplete="one-time-code"
            // Deliberately NOT `required`. `pattern` is kept and is still
            // correct alongside that: HTML exempts an empty value from a
            // pattern check, so the six-digit shape is enforced for a code that
            // IS typed while a password-only account can submit an empty field.
            pattern="[0-9]{6}"
            maxLength={6}
            value={totp}
            onChange={(event) => setTotp(event.target.value.replace(/\D/g, ''))}
          />
        </Field>

        <Button type="submit" disabled={busy}>
          {busy ? t('admin.common.loading') : t('admin.login.submit')}
        </Button>
      </form>
    </main>
  );
}
